import { DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { FunctionComponent, useRef } from 'react';
import { Account } from '../../services/models/account';
import { Modal } from '../ui';

interface CancelSubscriptionDialogProps {
  open: boolean;
  account: Account;
  isCanceling?: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

const CancelSubscriptionDialog: FunctionComponent<CancelSubscriptionDialogProps> = ({
  open,
  account,
  isCanceling,
  onAccept,
  onCancel,
}) => {
  const cancelButtonRef = useRef(null);

  const getActions = () => {
    return (
      <>
        <button
          type="button"
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={() => onAccept()}
          disabled={isCanceling}
        >
          {isCanceling && (
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          Cancel Subscription
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:w-auto sm:text-sm"
          onClick={() => onCancel()}
          ref={cancelButtonRef}
        >
          Cancel
        </button>
      </>
    );
  };

  return (
    <Modal isOpen={open} onClose={() => onCancel()}>
      <div className="sm:flex sm:items-start">
        <div className="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
        </div>
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <DialogTitle as="h3" className="text-lg leading-6 font-medium text-gray-900">
            Cancel your Newslit Subscription?
          </DialogTitle>
          <div className="mt-2">
            <p className="text-sm text-gray-500 m-o">
              Are you sure you want to cancel your{' '}
              <span className="font-bold">{account?.product?.name}</span> Subscription?
            </p>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">{getActions()}</div>
    </Modal>
  );
};

export default CancelSubscriptionDialog;
